import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { LayoutDashboard, FileText, Briefcase, Settings, Image, Mail, Menu, FileEdit, Tag, Sparkles, Home, User, Contact, Shield, Upload } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, } from "@/components/ui/dialog";
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
const API_URL = import.meta.env.VITE_API_URL || 'https://qbcron.com/api';
const AdminDashboard = () => {
    const navigate = useNavigate();
    const { toast } = useToast();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isChecking, setIsChecking] = useState(true);
    const [showProfileDialog, setShowProfileDialog] = useState(false);
    const [saving, setSaving] = useState(false);
    // Profile form state
    const [profileData, setProfileData] = useState({
        name: '',
        email: '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    useEffect(() => {
        console.log('[AdminDashboard] Checking authentication...');
        const token = localStorage.getItem('token');
        const userStr = localStorage.getItem('user');
        console.log('[AdminDashboard] Token exists:', !!token);
        if (!token) {
            console.log('[AdminDashboard] No token - redirecting to login');
            window.location.href = '/admin/login';
        }
        else {
            console.log('[AdminDashboard] Authenticated - showing dashboard');
            // Load user data
            if (userStr) {
                try {
                    const user = JSON.parse(userStr);
                    setProfileData(prev => ({
                        ...prev,
                        name: user.name || '',
                        email: user.email || ''
                    }));
                }
                catch (e) {
                    console.error('Error parsing user data:', e);
                }
            }
            setIsAuthenticated(true);
            setIsChecking(false);
        }
    }, []);
    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/admin/login';
    };
    const handleSaveProfile = async () => {
        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            // Validate passwords if changing
            if (profileData.newPassword) {
                if (!profileData.currentPassword) {
                    toast({
                        variant: "destructive",
                        title: "Error",
                        description: "Current password required to change password"
                    });
                    setSaving(false);
                    return;
                }
                if (profileData.newPassword !== profileData.confirmPassword) {
                    toast({
                        variant: "destructive",
                        title: "Error",
                        description: "New passwords do not match"
                    });
                    setSaving(false);
                    return;
                }
                if (profileData.newPassword.length < 6) {
                    toast({
                        variant: "destructive",
                        title: "Error",
                        description: "Password must be at least 6 characters"
                    });
                    setSaving(false);
                    return;
                }
            }
            // Update name and email
            const profileRes = await fetch(`${API_URL}/auth/profile`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    name: profileData.name,
                    email: profileData.email
                })
            });
            if (!profileRes.ok) {
                throw new Error('Failed to update profile');
            }
            const updatedUser = await profileRes.json();
            localStorage.setItem('user', JSON.stringify(updatedUser));
            // Change password if provided
            if (profileData.newPassword) {
                const passwordRes = await fetch(`${API_URL}/auth/change-password`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        currentPassword: profileData.currentPassword,
                        newPassword: profileData.newPassword
                    })
                });
                if (!passwordRes.ok) {
                    const error = await passwordRes.json();
                    throw new Error(error.error || 'Failed to change password');
                }
            }
            toast({
                title: "Success",
                description: "Profile updated successfully"
            });
            // Reset password fields
            setProfileData(prev => ({
                ...prev,
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            }));
            setShowProfileDialog(false);
        }
        catch (error) {
            toast({
                variant: "destructive",
                title: "Error",
                description: error.message || "Failed to update profile"
            });
        }
        finally {
            setSaving(false);
        }
    };
    // Show blocking screen while checking
    if (isChecking || !isAuthenticated) {
        return (_jsx("div", { style: {
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: '#1a1a1a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: '20px',
                zIndex: 9999
            }, children: _jsxs("div", { style: { textAlign: 'center' }, children: [_jsx("div", { children: "\uD83D\uDD12 Checking authentication..." }), _jsx("div", { style: { fontSize: '14px', marginTop: '10px', opacity: 0.7 }, children: "Please wait..." })] }) }));
    }
    const navItems = [
        { icon: LayoutDashboard, label: 'Overview', path: '/admin/dashboard' },
        { icon: Home, label: 'Edit Front Page', path: '/admin/frontpage' },
        { icon: Image, label: 'Slides', path: '/admin/slides' },
        { icon: FileText, label: 'Blog Posts', path: '/admin/blog' },
        { icon: Briefcase, label: 'Portfolio', path: '/admin/portfolio' },
        { icon: Tag, label: 'Categories', path: '/admin/categories' },
        { icon: Settings, label: 'Services', path: '/admin/services' },
        { icon: FileEdit, label: 'Custom Pages', path: '/admin/pages' },
        { icon: Upload, label: 'File Uploads', path: '/admin/files' },
        { icon: Mail, label: 'Contact Messages', path: '/admin/contact' },
        { icon: Contact, label: 'Contact Settings', path: '/admin/contact-settings' },
        { icon: Menu, label: 'Navigation Menu', path: '/admin/menu' },
        { icon: Shield, label: 'Security (MFA)', path: '/admin/security' },
        { icon: Sparkles, label: 'Site Settings', path: '/admin/settings' },
    ];
    return (_jsxs("div", { className: "min-h-screen bg-background", children: [_jsx("div", { className: "fixed top-0 left-64 right-0 h-14 bg-card border-b border-border z-40 flex items-center justify-end px-6", children: _jsxs(DropdownMenu, { children: [_jsxs(DropdownMenuTrigger, { className: "flex items-center gap-2 hover:bg-accent rounded-md px-3 py-2 transition-colors", children: [_jsx(User, { className: "h-5 w-5" }), _jsx("span", { className: "text-sm font-medium", children: profileData.name || 'Admin' })] }), _jsxs(DropdownMenuContent, { align: "end", className: "w-48", children: [_jsx(DropdownMenuLabel, { children: "My Account" }), _jsx(DropdownMenuSeparator, {}), _jsx(DropdownMenuItem, { onClick: () => setShowProfileDialog(true), children: "Edit Profile" }), _jsx(DropdownMenuItem, { onClick: handleLogout, children: "Logout" })] })] }) }), _jsx(Dialog, { open: showProfileDialog, onOpenChange: setShowProfileDialog, children: _jsxs(DialogContent, { children: [_jsxs(DialogHeader, { children: [_jsx(DialogTitle, { children: "Edit Profile" }), _jsx(DialogDescription, { children: "Update your profile information and change your password" })] }), _jsxs("div", { className: "space-y-4 py-4", children: [_jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "name", children: "Name" }), _jsx(Input, { id: "name", value: profileData.name, onChange: (e) => setProfileData(prev => ({ ...prev, name: e.target.value })), placeholder: "Your name" })] }), _jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "email", children: "Email" }), _jsx(Input, { id: "email", type: "email", value: profileData.email, onChange: (e) => setProfileData(prev => ({ ...prev, email: e.target.value })), placeholder: "your.email@example.com" })] }), _jsxs("div", { className: "border-t pt-4 mt-4", children: [_jsx("h4", { className: "text-sm font-medium mb-3", children: "Change Password" }), _jsxs("div", { className: "space-y-2 mb-3", children: [_jsx(Label, { htmlFor: "currentPassword", children: "Current Password" }), _jsx(Input, { id: "currentPassword", type: "password", value: profileData.currentPassword, onChange: (e) => setProfileData(prev => ({ ...prev, currentPassword: e.target.value })), placeholder: "Enter current password" })] }), _jsxs("div", { className: "space-y-2 mb-3", children: [_jsx(Label, { htmlFor: "newPassword", children: "New Password" }), _jsx(Input, { id: "newPassword", type: "password", value: profileData.newPassword, onChange: (e) => setProfileData(prev => ({ ...prev, newPassword: e.target.value })), placeholder: "Enter new password" })] }), _jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "confirmPassword", children: "Confirm New Password" }), _jsx(Input, { id: "confirmPassword", type: "password", value: profileData.confirmPassword, onChange: (e) => setProfileData(prev => ({ ...prev, confirmPassword: e.target.value })), placeholder: "Confirm new password" })] })] }), _jsxs("div", { className: "flex justify-end gap-3 pt-4", children: [_jsx("button", { onClick: () => setShowProfileDialog(false), className: "px-4 py-2 text-sm border rounded-md hover:bg-accent", disabled: saving, children: "Cancel" }), _jsx("button", { onClick: handleSaveProfile, disabled: saving, className: "px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50", children: saving ? 'Saving...' : 'Save Changes' })] })] })] }) }), _jsxs("aside", { className: "fixed left-0 top-0 h-full w-64 bg-card border-r border-border", children: [_jsx("div", { className: "p-6", children: _jsx("h1", { className: "text-2xl font-bold text-primary", children: "Qbcron Admin" }) }), _jsx("nav", { className: "px-4 space-y-2", children: navItems.map((item) => (_jsx(Link, { to: item.path, children: _jsxs(Button, { variant: "ghost", className: "w-full justify-start", children: [_jsx(item.icon, { className: "mr-2 h-4 w-4" }), item.label] }) }, item.path))) })] }), _jsx("main", { className: "ml-64 mt-14 p-8", children: _jsx(Outlet, {}) })] }));
};
export default AdminDashboard;
